/**
 * SP5 Wave 3 — fetches up to `limit` related benchmarks for a given source ID.
 *
 * Calls `GET /api/benchmarks/:id/related?limit=N` which applies the hybrid
 * scoring (topic_overlap DESC → publisher_match → report_type_match →
 * published_at DESC) server-side.
 */

import { useQuery } from '@tanstack/react-query'
import type {
  RelatedBenchmark,
  RelatedBenchmarksResponse,
} from '@intelhub/shared'
import { api } from '@/lib/api'

export function useRelatedBenchmarks(
  benchmarkId: string | undefined,
  limit = 5,
) {
  return useQuery<RelatedBenchmark[]>({
    queryKey: ['benchmarks', benchmarkId, 'related', limit],
    enabled: Boolean(benchmarkId),
    queryFn: async () => {
      const body = await api.get<RelatedBenchmarksResponse>(
        `/benchmarks/${benchmarkId}/related?limit=${limit}`,
      )
      return body.related
    },
    // 5-minute cache: related results are derived from the catalogue; stale-
    // while-revalidate is a good fit and keeps the detail page snappy on
    // back-navigation.
    staleTime: 5 * 60_000,
  })
}
